import React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  EditOutlined, TagOutlined, HourglassOutlined, CheckCircleOutlined,
  DollarOutlined, PauseCircleOutlined, CloseCircleOutlined, TrophyOutlined,
} from "@ant-design/icons";
import watiLogo from "../../assets/wati_logo_only.png";

dayjs.extend(utc);

export const PIPELINE_COLUMNS = [
  {
    key: "in_chat",
    label: "All Chats",
    color: "#2979e8",
    icon: <img src={watiLogo} alt="WATI" style={{ width: 15, height: 15, objectFit: "contain", display: "inline-block" }} />,
    isChat: true,
  },
  { key: "draft",                         label: "Draft",                        color: "#8c8c8c", icon: <EditOutlined />,         statuses: ["draft"] },
  { key: "waiting_for_price",             label: "Waiting For Price",            color: "#2979e8", icon: <TagOutlined />,           statuses: ["waiting_for_price"] },
  { key: "waiting_customer_confirmation", label: "Waiting Confirmation",         color: "#e4a527", icon: <HourglassOutlined />,     statuses: ["waiting_customer_confirmation"] },
  { key: "confirmed_waiting_payment",     label: "Confirmed · Awaiting Payment", color: "#27b6b6", icon: <CheckCircleOutlined />,   statuses: ["confirmed_waiting_payment"] },
  // Post-paid execution (purchase/delivery) is managed on the Operations page;
  // here a paid card shows a fulfilment progress pill and jumps straight to
  // Completed when delivery finishes.
  { key: "paid",                          label: "Paid",                         color: "#5ab82c", icon: <DollarOutlined />,        statuses: ["paid", "waiting_purchase", "purchased", "out_for_delivery"] },
  { key: "completed",                     label: "Completed",                    color: "#459922", icon: <TrophyOutlined />,        statuses: ["completed", "delivered"] },
  { key: "parked",                        label: "Parked",                       color: "#8c8c8c", icon: <PauseCircleOutlined />,   statuses: ["on_hold", "postponed", "parked"] },
  { key: "cancelled",                     label: "Lost",                         color: "#e85658", icon: <CloseCircleOutlined />,   statuses: ["cancelled"] },
];

export function groupQueriesByStatus(tasks) {
  return PIPELINE_COLUMNS.reduce((acc, col) => {
    acc[col.key] = col.isChat ? [] : tasks.filter(t => col.statuses?.includes(t.status));
    return acc;
  }, {});
}

export const URGENCY_CFG = {
  1: { color: "#595959", pill: "#f0f0f0", cardBg: "transparent",              label: "1 · Low" },
  2: { color: "#2979e8", pill: "#e6f4ff", cardBg: "rgba(22,119,255,0.04)",    label: "2 · Normal" },
  3: { color: "#c5871c", pill: "#fffbe6", cardBg: "rgba(250,173,20,0.06)",    label: "3 · Medium" },
  4: { color: "#c54522", pill: "#fff2e8", cardBg: "rgba(255,122,0,0.08)",     label: "4 · High" },
  5: { color: "#c12733", pill: "#fff1f0", cardBg: "rgba(255,77,79,0.12)",     label: "5 · Critical" },
};

export const urgencyMenuItems = Object.entries(URGENCY_CFG).map(([k, v]) => ({
  key: k,
  label: (
    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{
        display: "inline-block", width: 8, height: 8, borderRadius: "50%",
        background: v.color, flexShrink: 0,
      }} />
      {v.label}
    </span>
  ),
}));

export const PARKED_STATUSES = new Set(["parked", "on_hold", "postponed"]);

// Same "is this an international order" convention SendToSuppliersModal
// already uses to auto-check its own International order toggle: no local
// (971/UAE) prefix on the customer's number means international. Kept here
// so the board's filter and the composer's default agree on one definition
// instead of drifting.
export function isInternationalCard(card) {
  const phone = (card?.customer?.customer_mobile || card?.customer_mobile || "").replace(/^\+/, "");
  return !!phone && !phone.startsWith("971");
}

// Shared by both "Bring to Today" entry points (WorkflowCard.js's 3-dot
// menu, CustomerEditDrawer.js's Queries tab row action) so the eligibility
// rule lives in exactly one place instead of two copies drifting apart.
export function isDatedToday(dateStr) {
  if (!dateStr) return false;
  return dayjs(dateStr).isSame(dayjs(), "day");
}

// completed/delivered queries are finished, not "incomplete due to
// whatever reason" — cancelled ones are deliberately still eligible, a
// lost query might be worth a second attempt.
export function canBringToToday(card) {
  return !["completed", "delivered"].includes(card?.status) && !isDatedToday(card?.created_at);
}

export const WORKFLOW_CARD_HEIGHT = {
  BASE: 70,
  ASSIGNEE_ROW: 20,
  DETAILS_ROW: 66,
  SENDING_PROGRESS: 32,
  INFO_BLOCK_CHROME: 20,
  INFO_LINE: 18,
  INFO_LINE_GAP: 2,
  REMARKS_CHARS_PER_LINE: 42,
};

function estimateRemarksLines(remarks) {
  if (!remarks) return 0;
  return String(remarks).split("\n").reduce((total, line) => (
    total + Math.max(1, Math.ceil(line.length / WORKFLOW_CARD_HEIGHT.REMARKS_CHARS_PER_LINE))
  ), 0);
}

export function estimateCardHeight(card, sendingProgress = null) {
  let height = WORKFLOW_CARD_HEIGHT.BASE;
  const assignee = card?.assigned_user;
  const assigneeName = assignee
    ? (assignee.name ? `${assignee.name}${assignee.surname ? ` ${assignee.surname}` : ""}` : assignee.username)
    : null;
  const createdDate = card?.created_at ? dayjs(card.created_at) : null;
  const customerName = card?.customer?.customer_name || card?.customer_name;
  const customerMobile = card?.customer?.customer_mobile || card?.customer_mobile;

  if (assigneeName || createdDate?.isValid()) {
    height += WORKFLOW_CARD_HEIGHT.ASSIGNEE_ROW;
  }
  if (card?.vehicle || customerName || customerMobile) {
    height += WORKFLOW_CARD_HEIGHT.DETAILS_ROW;
  }
  if (sendingProgress) {
    height += WORKFLOW_CARD_HEIGHT.SENDING_PROGRESS;
  }

  const isParked = PARKED_STATUSES.has(card?.status);
  if (isParked && (card?.postponed_until || card?.remarks)) {
    height += WORKFLOW_CARD_HEIGHT.INFO_BLOCK_CHROME;
    if (card.postponed_until) {
      height += WORKFLOW_CARD_HEIGHT.INFO_LINE;
    }
    const remarksLines = estimateRemarksLines(card.remarks);
    if (remarksLines > 0) {
      if (card.postponed_until) height += WORKFLOW_CARD_HEIGHT.INFO_LINE_GAP;
      height += remarksLines * WORKFLOW_CARD_HEIGHT.INFO_LINE;
    }
  }

  return height;
}

// Decides whether a socket-delivered task should be visible under the
// Kanban board's own agent filter / date range (QuerysetV4.js's
// selectedAgents/dateRange, sent server-side as assigned_to/from/to on
// fetchQueries) — mirrors matchesChatFilters in helpers.js (the same
// problem on the WATI chat sidebar: a socket update for a task outside the
// currently-loaded, filtered set used to get inserted anyway with
// incomplete data, e.g. no customer info, since it only exists in the
// board's own filtered fetch). Used both to gate adding a not-yet-loaded
// task and to drop an existing one that no longer matches after a patch
// (e.g. reassigned away from the filtered agent).
// Missing data (no assigned_to/assigned_user on the payload at all, e.g. a
// query_task_movements row) falls through as a match on that check alone —
// safer to show a card than to hide it because a narrower event payload
// didn't happen to carry the field.
export function matchesQuerysetFilters(task, { assigneeIds = [], dateRange = null } = {}) {
  if (assigneeIds.length > 0) {
    const assignedId = task.assigned_to ?? task.assigned_user?.id ?? null;
    if (assignedId != null && !assigneeIds.includes(assignedId)) return false;
  }
  if (dateRange) {
    const [from, to] = dateRange;
    const created = task.created_at ? dayjs(task.created_at) : null;
    if (created?.isValid() && (created.isBefore(from.startOf("day")) || created.isAfter(to.endOf("day")))) {
      return false;
    }
  }
  return true;
}

// `remarks` accumulates every remark ever added to the task (parked reasons,
// cancellation reason, etc.) as newline-joined text — the cancel endpoint
// appends "[timestamp] Cancelled: <reason>" as the latest line. Pull just
// that reason back out rather than showing the whole accumulated history.
export function lastCancelReason(remarks) {
  if (!remarks) return null;
  const lines = String(remarks).split("\n").filter(Boolean);
  const last = lines[lines.length - 1] || "";
  const m = last.match(/Cancelled:\s*(.+)$/);
  return (m ? m[1] : last).trim() || null;
}

// Per-part fulfilment progress. Ops work happens while the card sits in "paid"
// on the sales pipeline (the Operations page owns the post-paid flow), so paid
// cards show progress toward the next incomplete milestone to keep sales
// informed. Legacy fulfilment statuses keep their milestone-specific view.
export function fulfilmentProgress(card) {
  const s = card.fulfillment_summary;
  if (!s || !s.total) return null;
  const delivered = s.delivered ?? 0;
  const out = (s.out_for_delivery ?? 0) + delivered;
  const purchased = (s.purchased ?? 0) + out;
  if (card.status === "paid") {
    if (purchased < s.total) return { done: purchased, total: s.total, label: "purchased" };
    if (out < s.total) return { done: out, total: s.total, label: "out for delivery" };
    return { done: delivered, total: s.total, label: "delivered" };
  }
  if (s.total < 2) return null;
  if (card.status === "waiting_purchase") return { done: purchased, total: s.total, label: "purchased" };
  if (card.status === "purchased") return { done: out, total: s.total, label: "out for delivery" };
  if (card.status === "out_for_delivery") return { done: delivered, total: s.total, label: "delivered" };
  return null;
}

// Pure calculation behind the parked-task countdown badge — takes `now`
// explicitly so callers can either compute it once (a table cell) or tick it
// every minute via their own timer (the Kanban card).
export function parkCountdownInfo(until, now = dayjs.utc()) {
  const target = dayjs.utc(until.replace(" ", "T"));
  const diffMins = target.diff(now, "minute");
  const absDate = target.local().format("D MMM, h:mm A");

  if (diffMins > 0) {
    const h = Math.floor(diffMins / 60);
    const m = diffMins % 60;
    return { label: absDate, badge: `in ${h > 0 ? `${h}h ` : ""}${m}m`, color: "#2979e8", overdue: false };
  }
  if (diffMins === 0) {
    return { label: absDate, badge: "due now", color: "#e4a527", overdue: false };
  }
  const over = Math.abs(diffMins);
  const h = Math.floor(over / 60);
  const m = over % 60;
  return { label: absDate, badge: `${h > 0 ? `${h}h ` : ""}${m}m overdue`, color: "#e85658", overdue: true };
}
